import React, { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import type { ScheduledSession } from '@fs/zion-rootstech'
import { useMyScheduleContext } from './MyScheduleProvider'
import { useActivateCalendarData, useCalendarDataContext } from './CalendarDataProvider'
import { mapToMyScheduleDate, mapToMyScheduleHour } from '../components/schedule/helpers/scheduleHelpers'
import {
  hasScheduleSignInIntent,
  consumeScheduleSignInIntent,
} from '../components/schedule/helpers/pending-schedule-intent'
import useScheduleStatus from '../components/schedule/hooks/useScheduleStatus'

const QUERY_KEYS = ['addToSchedule', 'scheduleCalendarDate', 'scheduleDate', 'scheduleTime', 'scheduleIntent'] as const

/** Validates the selected timestamp and its UTC slot keys. */
function getValidatedPendingAdd(query: Record<string, unknown> | undefined): ScheduledSession | null {
  const contentId = typeof query?.addToSchedule === 'string' ? query.addToSchedule : ''
  const timestamp = query?.scheduleCalendarDate
  const scheduleDate = query?.scheduleDate
  const scheduleTime = query?.scheduleTime

  if (!contentId.trim() || typeof timestamp !== 'string' || !/^\d+$/.test(timestamp)) {
    return null
  }

  const calendarDate = Number(timestamp)
  if (!Number.isSafeInteger(calendarDate) || calendarDate <= 0) {
    return null
  }

  const selectedDate = new Date(calendarDate)
  if (Number.isNaN(selectedDate.getTime())) {
    return null
  }

  const expectedDate = mapToMyScheduleDate(selectedDate)
  const expectedTime = mapToMyScheduleHour(selectedDate)

  if (scheduleDate !== expectedDate || scheduleTime !== expectedTime) {
    return null
  }

  const session = { contentId, calendarDate, date: expectedDate, time: expectedTime }
  return hasScheduleSignInIntent(query?.scheduleIntent, session) ? session : null
}

type AutoAddToScheduleProviderProps = {
  children: ReactNode
}

export default function AutoAddToScheduleProvider({ children }: AutoAddToScheduleProviderProps): JSX.Element {
  const { query, removeQuery } = useQueryParams({ replaceState: true })
  const { signedIn, cisId, id } = useUser()
  const identity = JSON.stringify([signedIn, cisId, id])
  const identityRef = useRef({ identity, generation: 0 })
  if (identityRef.current.identity !== identity) {
    identityRef.current = { identity, generation: identityRef.current.generation + 1 }
  }
  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const { addToMySchedule, isReady, savedSchedule } = useMyScheduleContext()
  const { calendarData, loading: calendarLoading, error: calendarError, isActive } = useCalendarDataContext()
  const pendingAdd = useMemo(() => getValidatedPendingAdd(query), [query])
  useActivateCalendarData(Boolean(pendingAdd) && isReady)
  const processedRequest = useRef<string | null>(null)
  const { showAddedOverlay, showPresentOverlay, showErrorOverlay } = useScheduleStatus()

  useEffect(() => {
    if (!QUERY_KEYS.some((key) => key in query)) {
      processedRequest.current = null
      return
    }
    const value = pendingAdd
    if (!value) {
      removeQuery(...QUERY_KEYS)
      return
    }

    if (!isReady) {
      return
    }

    const requestKey = JSON.stringify(value)
    if (processedRequest.current === requestKey) {
      return
    }
    if (calendarError) {
      processedRequest.current = requestKey
      removeQuery(...QUERY_KEYS)
      if (consumeScheduleSignInIntent(query.scheduleIntent, value)) showErrorOverlay(calendarError)
      return
    }
    if (!isActive || calendarLoading) return
    processedRequest.current = requestKey
    removeQuery(...QUERY_KEYS)
    if (!consumeScheduleSignInIntent(query.scheduleIntent, value)) return
    const { contentId, calendarDate, date, time } = value
    if (!calendarData.some((entry) => entry.date === calendarDate && entry.item?.contentId === contentId)) {
      showErrorOverlay()
      return
    }
    const slot = savedSchedule?.days[date]?.[time]
    if (slot?.preferred === contentId || slot?.alternates?.includes(contentId)) {
      showPresentOverlay()
      return
    }
    const generation = identityRef.current.generation
    const canShowFeedback = (): boolean => mountedRef.current && identityRef.current.generation === generation
    addToMySchedule(value)
      .then(() => {
        if (!canShowFeedback()) return
        showAddedOverlay()
      })
      .catch((error: unknown) => {
        if (!canShowFeedback()) return
        showErrorOverlay(error)
      })
  }, [
    addToMySchedule,
    calendarData,
    calendarError,
    calendarLoading,
    isActive,
    isReady,
    pendingAdd,
    query,
    removeQuery,
    savedSchedule,
    showAddedOverlay,
    showPresentOverlay,
    showErrorOverlay,
  ])

  return <>{children}</>
}
